// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Real operator auth for the Query Health console. Uses Google SSO + TOTP MFA.
// SMS second factor is NOT wired in the pilot — TOTP only.
// TODO(SMS): wire PhoneMultiFactorGenerator + RecaptchaVerifier when SMS MFA is needed.

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import {
  onAuthStateChanged,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  getIdTokenResult,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  type User,
  type MultiFactorResolver,
} from 'firebase/auth'
import { auth, isFirebaseConfigured } from '@/lib/firebase'
import { bootstrapSession } from '@/lib/api'
import type { Role } from '@/types'

// The set of valid Role values — used for runtime validation of custom claims.
const VALID_ROLES: ReadonlySet<string> = new Set<Role>([
  'platform_superadmin',
  'qh_admin',
  'org_admin',
  'clinic_staff',
  'provider',
])

function isRole(v: unknown): v is Role {
  return typeof v === 'string' && VALID_ROLES.has(v)
}

interface AuthContextValue {
  user: User | null
  loading: boolean
  role: Role | null
  isPlatform: boolean
  mfaResolver: MultiFactorResolver | null
  authError: string | null
  clearAuthError: () => void
  signInWithGoogle: () => Promise<void>
  completeTotpMfa: (verificationCode: string) => Promise<void>
  cancelMfa: () => void
  signOutUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [role, setRole] = useState<Role | null>(null)
  const [isPlatform, setIsPlatform] = useState(false)
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null)
  const [authError, setAuthError] = useState<string | null>(null)

  useEffect(() => {
    // When Firebase is not configured (missing env vars), onAuthStateChanged will
    // never fire, leaving `loading` stuck at `true` and RequireAuth rendering
    // <AppLoader /> indefinitely. Guard here: resolve to signed-out state
    // immediately so the app renders LoginPage instead of hanging.
    if (!isFirebaseConfigured) {
      setLoading(false)
      return
    }

    // Track the returning redirect so we don't flash /login while it completes:
    // onAuthStateChanged can fire with null before getRedirectResult restores the
    // session. We hold the loader until the redirect settles, then only fall through
    // to signed-out if there's still no user.
    let currentUser: User | null = null
    let redirectSettled = false

    // Complete a federated redirect if we're returning from one. Now that sign-in
    // is a top-level redirect (not a popup), the MFA challenge and any auth errors
    // surface here rather than from signInWithGoogle. The tenant is statically
    // pinned in firebase.ts at module load, so it's already applied before this runs.
    getRedirectResult(auth)
      .catch((err: unknown) => {
        if (
          err !== null &&
          typeof err === 'object' &&
          'code' in err &&
          (err as { code: string }).code === 'auth/multi-factor-auth-required'
        ) {
          setMfaResolver(getMultiFactorResolver(auth, err as Parameters<typeof getMultiFactorResolver>[1]))
          return
        }
        setAuthError(authErrorKey(err))
      })
      .finally(() => {
        redirectSettled = true
        // Redirect done and still no user → genuinely signed out; release the loader.
        if (currentUser === null) setLoading(false)
      })

    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      currentUser = u
      setUser(u)
      if (u) {
        try {
          // Read the cached claim first (no network round-trip).
          const result = await getIdTokenResult(u)
          const claimedRole = result.claims['role']

          if (isRole(claimedRole)) {
            // Happy path — role claim already stamped (e.g. page reload after binding).
            setRole(claimedRole)
            setIsPlatform(
              claimedRole === 'platform_superadmin' || claimedRole === 'qh_admin',
            )
          } else {
            // First login under federated SSO: the operator's firebase_uid hasn't
            // been bound yet and no role claim exists. Call bootstrap to bind the
            // uid and stamp claims, then force-refresh the token to pick them up.
            await bootstrapSession()
            const refreshed = await getIdTokenResult(u, true)
            const refreshedRole = refreshed.claims['role']
            if (isRole(refreshedRole)) {
              setRole(refreshedRole)
              setIsPlatform(
                refreshedRole === 'platform_superadmin' || refreshedRole === 'qh_admin',
              )
            } else {
              setRole(null)
              setIsPlatform(false)
            }
          }
        } catch {
          // Bootstrap 401/403, network error, or token refresh failure.
          // Resolves to AccessDenied — correct for a non-invited Google account.
          setRole(null)
          setIsPlatform(false)
        }
        setLoading(false)
      } else {
        setRole(null)
        setIsPlatform(false)
        // Signed out: hold the loader (don't flash /login) until the returning
        // redirect has settled. If it never settles with a user, the getRedirectResult
        // .finally above releases the loader.
        if (redirectSettled) setLoading(false)
      }
    })
    return unsubscribe
  }, [])

  async function signInWithGoogle(): Promise<void> {
    // Top-level redirect (not popup): under Safari ITP / Chrome storage
    // partitioning a popup authenticates in a partitioned context and leaves the
    // parent window signed-out (silent failure). A redirect stays first-party —
    // it requires authDomain on the app's registrable domain (auth.<domain>, set
    // via VITE_FIREBASE_AUTH_DOMAIN). Navigates away; the MFA challenge and errors
    // are handled on return by getRedirectResult.
    setAuthError(null)
    await signInWithRedirect(auth, new GoogleAuthProvider())
  }

  async function completeTotpMfa(verificationCode: string): Promise<void> {
    if (!mfaResolver) {
      throw new Error('No MFA resolver — call signInWithGoogle first')
    }
    const hint = mfaResolver.hints[0]
    if (!hint) {
      throw new Error('MFA resolver has no hints')
    }
    if (hint.factorId !== TotpMultiFactorGenerator.FACTOR_ID) {
      // SMS second factor is not wired in the pilot — TOTP only.
      // TODO(SMS): wire PhoneMultiFactorGenerator + RecaptchaVerifier
      throw new Error(`Unsupported MFA factor: ${hint.factorId}. Only TOTP is supported in this pilot.`)
    }
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, verificationCode)
    await mfaResolver.resolveSignIn(assertion)
    setMfaResolver(null)
  }

  function cancelMfa(): void {
    setMfaResolver(null)
  }

  async function signOutUser(): Promise<void> {
    await signOut(auth)
  }

  const value: AuthContextValue = {
    user,
    loading,
    role,
    isPlatform,
    mfaResolver,
    authError,
    clearAuthError: () => setAuthError(null),
    signInWithGoogle,
    completeTotpMfa,
    cancelMfa,
    signOutUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}

// Maps Firebase auth error codes to i18n keys used by the Login page.
// auth/multi-factor-auth-required is handled in signInWithGoogle; it's
// intentionally absent here since it should never be shown as an error.
export function authErrorKey(err: unknown): string {
  const code = typeof err === 'object' && err !== null && 'code' in err
    ? (err as { code: string }).code
    : ''

  switch (code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'login.popupClosed'
    case 'auth/popup-blocked':
      return 'login.popupBlocked'
    case 'auth/unauthorized-domain':
      return 'login.unauthorizedDomain'
    case 'auth/account-exists-with-different-credential':
      return 'login.accountConflict'
    case 'auth/invalid-verification-code':
    case 'auth/missing-verification-code':
      return 'login.invalidMfaCode'
    case 'auth/network-request-failed':
      return 'login.networkError'
    case 'auth/too-many-requests':
      return 'login.tooManyRequests'
    case 'auth/user-disabled':
      return 'login.accountDisabled'
    default:
      return 'login.genericError'
  }
}
